import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocalStorage, useNewTranslation } from 'hooks'
import { createGetCampaignsList, createGetRequestPagination, selectProfile } from 'state'
import {
    selectCountryFilter,
    selectTeamFilter,
    selectTeamTypeFilters,
    selectIsHelpdeskSupportedByActiveTeam,
} from 'state/filter'
import { selectIsCampaignsEmpty, selectIsInternalUser } from 'state/profile'
import { CampaignState, requestCampaignList, CampaignViews, CampaignsActions } from 'state/campaigns'
import EmptyState from 'components/Shared/EmptyState'
import CampaignCard from './CampaignCard'
import { CampaignListLayout, StyledLoading } from './style'
import { duplicateCampaign, deleteCampaign } from 'state/campaigns'
import { selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import Modal, { ModalProps } from 'particles/Modal'
import { useCheckSomePermissions } from 'hooks/permissions'
import { CampaignManagement, TemplatedCampaignManagement } from 'components/Shared/Permission/enums'
import GetStartedSvg from 'icons/GetStarted.svg'
import Icon from 'components/Icon/Icon'
import { emitShowTooltip } from 'services/tooltipEmitter'

interface CampaignsProp {
    searchTerm: string | null
    handleClearSearchTerm: () => void
    isLoading: boolean
    viewType: CampaignViews
    switchViewType: (type: CampaignViews) => void
    onCreateCampaign: () => void
}

const Campaigns: React.FC<CampaignsProp> = ({
    searchTerm,
    handleClearSearchTerm,
    isLoading,
    viewType,
    switchViewType,
    onCreateCampaign,
}) => {
    const [loadMore, setLoadMore] = useState<boolean>(false)
    const [modalState, setModalState] = useState<ModalProps | null>()
    const [t] = useNewTranslation()
    const countryFilter = useSelector(selectCountryFilter)
    const teamTypeFilters = useSelector(selectTeamTypeFilters)
    const isHelpdeskSupported = useSelector(selectIsHelpdeskSupportedByActiveTeam)
    const profile = useSelector(selectProfile)
    const getCampaignList = useSelector(createGetCampaignsList)
    const dispatch = useDispatch()
    const teamFilter = useSelector(selectTeamFilter)
    const isInternalUser = useSelector(selectIsInternalUser)
    const campaignsList = getCampaignList(viewType)
    const isRequestsLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.DuplicateCampaign]))
    const hasCreateNewCampaignPermission = useCheckSomePermissions([
        CampaignManagement.CreateAndEditBriefsForInternal,
        CampaignManagement.CreateAndEditBriefsForExternal,
        TemplatedCampaignManagement.CreateAndEditBriefsForInternal,
        TemplatedCampaignManagement.CreateAndEditBriefsForExternal,
    ])
    const isCampaignsEmpty = useSelector(selectIsCampaignsEmpty)
    const teamTypeFiltersAmount = teamTypeFilters?.campaign_list?.selected.length
    const latestHandleScrollRef = useRef<(dom: HTMLElement) => void>()
    const pagination = useSelector(createGetRequestPagination(STATE_KEYS.CampaignsList))
    const { hasMore } = pagination
    const [helpdeskTooltipIndicator, setHelpdeskTooltipIndicator] = useLocalStorage<Record<string, string>>(
        'helpdesk-tooltip',
        {},
    )

    const fetchData = (loadMore = false) => {
        dispatch(
            requestCampaignList({
                loadMore,
                viewType,
                keywords: searchTerm,
                country: countryFilter,
                team_type: teamTypeFilters?.campaign_list?.selected,
            }),
        )
        setLoadMore(loadMore)
    }

    const handleScroll = (dom: HTMLElement) => {
        if (!hasMore) return
        if (!dom || dom.scrollTop === 0) return
        if (dom.scrollTop + window.innerHeight > dom.scrollHeight - 1) {
            fetchData(true)
        }
    }

    const handleDuplicate = (uuid: string) => {
        dispatch(
            duplicateCampaign({
                uuid,
                onSuccessCallback: () => switchViewType('live'),
            }),
        )
    }

    const handleDelete = (uuid: string) => {
        dispatch(
            deleteCampaign({
                uuid,
            }),
        )
    }

    const openDeleteModal = (campaign: CampaignState) => {
        setModalState({
            title: t('modalWindow.campaign.deleteCampaignHeader', { campaignName: campaign?.title }),
            subtext: t('modalWindow.campaign.deleteCampaignSubtext'),
            secondaryButtonProps: { label: t('button.noCancel') },
            primaryButtonProps: { label: t('button.delete') },
            size: 'm',
            onConfirm: () => {
                handleDelete(campaign.uuid)
                setModalState(null)
            },
            onClose: () => {
                setModalState(null)
            },
        })
    }

    const openStartModal = () => {
        setModalState({
            title: t('modalWindow.signUp.getStartedTitle'),
            subtext: t('modalWindow.signUp.getStartedSubTitle'),
            primaryButtonProps: { label: t('button.createCampaign') },
            size: 'm',
            hasSecondaryButton: false,
            fitPrimaryButton: true,
            img: GetStartedSvg,
            onConfirm: () => {
                onCreateCampaign()
                setModalState(null)
            },
            onClose: () => {
                if (isHelpdeskSupported && !!profile.uuid && !helpdeskTooltipIndicator[profile.uuid]) {
                    emitShowTooltip('helpdesk')
                    setHelpdeskTooltipIndicator({
                        ...helpdeskTooltipIndicator,
                        [profile.uuid]: '1',
                    })
                }
                setModalState(null)
            },
        })
    }

    useEffect(() => {
        latestHandleScrollRef.current = handleScroll
    }, [hasMore])

    useEffect(() => {
        if (isCampaignsEmpty && hasCreateNewCampaignPermission) {
            openStartModal()
        } else {
            if (isHelpdeskSupported && !!profile.uuid && !helpdeskTooltipIndicator[profile.uuid]) {
                emitShowTooltip('helpdesk')
                setHelpdeskTooltipIndicator({
                    ...helpdeskTooltipIndicator,
                    [profile.uuid]: '1',
                })
            }
        }
    }, [])

    useEffect(() => {
        if (viewType) {
            fetchData(false)
        }
        return () => {
            dispatch({ type: CampaignsActions.CAMPAIGN_LIST_CLEAR })
        }
    }, [viewType, dispatch, searchTerm, countryFilter, teamFilter, isRequestsLoading, teamTypeFiltersAmount])

    useEffect(() => {
        const dom = window.document.getElementById('inner-content')
        if (!dom) return
        const onScroll = (dom: HTMLElement) => {
            if (latestHandleScrollRef.current) {
                latestHandleScrollRef.current(dom)
            }
        }
        dom.addEventListener('scroll', () => onScroll(dom))
        return () => dom.removeEventListener('scroll', () => onScroll(dom))
    }, [])

    const shouldDisplay = campaignsList.length > 0

    if (isRequestsLoading) return <StyledLoading />

    const emptyStateValue = (() => {
        if (teamTypeFiltersAmount > 0) {
            return {
                title: t('submission.review.filterNoResult.title'),
                body: t('submission.review.filterNoResult.subtext'),
                icon: <Icon.EmptyFilterStateIcon />,
            }
        }
        if (viewType === 'live') {
            return {
                title: t('emptyState.noLiveCampaignsTitle'),
                body: t('emptyState.noLiveCampaignsSubtitle'),
            }
        }

        return {
            title: t('emptyState.noClosedCampaignsTitle'),
            body: t('emptyState.noClosedCampaignsSubtitle'),
        }
    })()

    return (
        <CampaignListLayout>
            {shouldDisplay ? (
                <div className="campaigns-card-layout">
                    {campaignsList.map((campaign: CampaignState) => {
                        const campaignType =
                            isInternalUser && campaign.self_serve ? t('campaignEditor.campaignType.selfServe') : null

                        const displayTypeLabel = campaign.in_template_flow
                            ? t('card.templateCampaign.label')
                            : campaignType
                        return (
                            <CampaignCard
                                key={campaign.uuid}
                                campaign={campaign}
                                onDuplicate={handleDuplicate}
                                campaignTypeLabel={displayTypeLabel}
                                type={campaign.in_template_flow ? 'template' : 'campaign'}
                                onDelete={openDeleteModal}
                            />
                        )
                    })}
                </div>
            ) : (
                <EmptyState
                    isLoading={isLoading}
                    width="100%"
                    title={emptyStateValue.title}
                    body={emptyStateValue.body}
                    titleColor="primary"
                    bodyColor="primary"
                    customIcon={emptyStateValue.icon}
                />
            )}
            {isLoading && <StyledLoading loadMore={loadMore} />}
            {modalState && <Modal {...modalState} />}
        </CampaignListLayout>
    )
}

export default Campaigns
